// Runtime SQL usando sql.js (SQLite compilado a WASM corriendo en el browser).
//
// Flow:
//   1. Crear DB en memoria
//   2. Correr setup SQL del ejercicio (CREATE TABLE, INSERT, etc.)
//   3. Correr query del candidato
//   4. Comparar resultado vs expectedRows del test

import sqlWasmUrl from "sql.js/dist/sql-wasm.wasm?url";
import type { RunResult, RuntimeInput, SqlTest, TestRunResult } from "./types";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SqlJsModule = any;

/**
 * Cache del módulo sql.js inicializado. Cargamos vía dynamic import + unwrap
 * para soportar las distintas formas de export ESM que tuvo sql.js entre
 * versiones (1.11 default export, 1.14 ESM browser con named export distinto, etc.)
 */
let sqlJsPromise: Promise<SqlJsModule> | null = null;

async function loadSqlJs(): Promise<SqlJsModule> {
  if (!sqlJsPromise) {
    sqlJsPromise = (async () => {
      const mod = await import("sql.js");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const m = mod as any;
      const init = m.default ?? m.initSqlJs ?? m;
      if (typeof init !== "function") {
        throw new Error(
          "No se pudo encontrar initSqlJs en sql.js (export inesperado)."
        );
      }
      return await init({ locateFile: () => sqlWasmUrl });
    })();
  }
  return sqlJsPromise;
}

interface RunSqlParams extends RuntimeInput {
  exerciseSetupSql: string;
  exerciseSqlTests: SqlTest[];
}

export async function runSqlExercise(params: RunSqlParams): Promise<RunResult> {
  const { code, exerciseSetupSql, exerciseSqlTests } = params;

  if (!exerciseSqlTests?.length) {
    return {
      results: [],
      passedCount: 0,
      totalCount: 0,
      score: 0,
      fatalError: "Este ejercicio no tiene tests SQL configurados.",
    };
  }

  let SQL;
  try {
    SQL = await loadSqlJs();
  } catch (e) {
    return {
      results: [],
      passedCount: 0,
      totalCount: 0,
      score: 0,
      fatalError: `No se pudo cargar el runtime SQL: ${
        e instanceof Error ? e.message : String(e)
      }`,
    };
  }

  const results: TestRunResult[] = [];
  let passed = 0;

  for (const test of exerciseSqlTests) {
    let db: SqlDb | null = null;
    let ok = false;
    let err: string | undefined;
    try {
      db = new SQL.Database() as SqlDb;
      if (exerciseSetupSql?.trim()) {
        db.exec(exerciseSetupSql);
      }
      const rows = runQueryAsObjects(db, code);
      ok = rowsMatch(rows, test.expectedRows, test.ignoreOrder ?? false);
      if (!ok) {
        err = `Esperaba ${JSON.stringify(test.expectedRows)}, devolviste ${JSON.stringify(rows)}.`;
      }
    } catch (e) {
      err = e instanceof Error ? e.message : String(e);
    } finally {
      db?.close();
    }
    if (ok) passed++;
    results.push({ name: test.name, passed: ok, error: err });
  }

  return {
    results,
    passedCount: passed,
    totalCount: exerciseSqlTests.length,
    score:
      exerciseSqlTests.length === 0
        ? 0
        : Math.round((passed / exerciseSqlTests.length) * 100),
  };
}

interface SqlDb {
  exec(sql: string): Array<{ columns: string[]; values: unknown[][] }>;
  close(): void;
}

function runQueryAsObjects(
  db: SqlDb,
  sql: string
): Array<Record<string, unknown>> {
  const res = db.exec(sql);
  if (res.length === 0) return [];
  // Tomamos el último statement con resultados (ej. después de varios SELECTs, el último)
  const last = res[res.length - 1];
  return last.values.map((row) => {
    const obj: Record<string, unknown> = {};
    last.columns.forEach((col, i) => {
      obj[col] = row[i];
    });
    return obj;
  });
}

function rowsMatch(
  actual: Array<Record<string, unknown>>,
  expected: Array<Record<string, unknown>>,
  ignoreOrder: boolean
): boolean {
  if (actual.length !== expected.length) return false;
  if (ignoreOrder) {
    // Sort por JSON stringify deterministico
    const a = [...actual].map(stringifyDeterministic).sort();
    const b = [...expected].map(stringifyDeterministic).sort();
    return a.every((s, i) => s === b[i]);
  }
  return actual.every((r, i) => deepEqual(r, expected[i]));
}

function stringifyDeterministic(o: Record<string, unknown>): string {
  return JSON.stringify(
    Object.keys(o)
      .sort()
      .reduce((acc, k) => {
        acc[k] = o[k];
        return acc;
      }, {} as Record<string, unknown>)
  );
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== typeof b) return false;
  if (a == null || b == null) return false;
  if (typeof a === "object" && typeof b === "object") {
    const aKeys = Object.keys(a as object).sort();
    const bKeys = Object.keys(b as object).sort();
    if (aKeys.length !== bKeys.length) return false;
    if (!aKeys.every((k, i) => k === bKeys[i])) return false;
    return aKeys.every((k) =>
      deepEqual((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k])
    );
  }
  return false;
}
